import React, { useRef, useState, useEffect, useMemo, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { useEffectOnce } from 'react-use';
import { matchSorter } from 'match-sorter';
import { MdSearch } from 'react-icons/md';
import { Container, Flex, Box, Text, Input } from '../../design-system';
import { useData } from '../../helpers/data-loader';
import { errorMsg, checkPermissions } from '../../helpers/utils';
import NotAccessible from '../../components/NotAccessible';
import Sidebar from './Sidebar';
import generateInnerSideBarViews from './Sidebar/generateInnerSideBarViews';
import { PERMISSIONS } from '../../globalConstants';
import { Session } from '../../contexts/sessionContext';

const SideBarContainer = styled(Box)`
  height: 50rem;
  overflow-x: scroll;
`;

const Vault = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);

  const [searchText, setSearchText] = useState('');
  const [showText, toggleTextVisibility] = useState(false);
  const [clientData, fetchData] = useData('/client', {
    errorMsg: errorMsg('clients'),
  });

  const scrollDiv = useRef<HTMLDivElement>(null);
  const initialRender = useRef(true);
  const canEdit = checkPermissions(permissions, [PERMISSIONS.UPDATE_VAULT]);

  useEffectOnce(() => {
    fetchData();
  });

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    }
    if (scrollDiv.current) {
      scrollDiv.current.scrollTop = 0;
    }
  }, [pathname]);

  const data = useMemo(() => {
    return matchSorter(clientData, searchText, {
      keys: [
        {
          threshold: matchSorter.rankings.CONTAINS,
          key: 'name',
        },
      ],
    });
  }, [clientData, searchText]);
  const selectedClient =
    (clientData.length > 0 &&
      clientData.find(
        (item) => item.id === parseInt(pathname.split('/')[2], 10),
      )) ||
    {};
  if (
    !isEmpty(clientData) &&
    isEmpty(selectedClient) &&
    pathname !== '/vault'
  ) {
    return <NotAccessible text="You are not permitted to view this page." />;
  }

  const views = generateInnerSideBarViews({
    client: selectedClient,
    fetchData,
  });
  // Match on each view's own route (exact, or as a prefix for nested paths
  // like /vault/:id/program-case-statements/:programId) so the mapping
  // can't silently break if generateInnerSideBarViews is reordered.
  const currentView =
    views.find((view) => view.route === pathname) ??
    views.find((view) => pathname.startsWith(`${view.route}/`));

  return (
    <Container p={25} fluid gutter={25}>
      {clientData.length > 0 && (
        <Flex>
          <SideBarContainer mr={2} ref={scrollDiv}>
            <Text fontWeight={600}>Select a Client</Text>
            <Box mr={20} mt={15}>
              <Input
                name="search"
                floating
                label={
                  <Flex alignItems="center">
                    <MdSearch />
                    {!showText && (
                      <Text ml={1} fontSize={14}>
                        Type here to search...
                      </Text>
                    )}
                  </Flex>
                }
                onChange={(__, val) => setSearchText(val)}
                value={searchText}
                onFocus={() => toggleTextVisibility(true)}
                onBlur={() => !searchText && toggleTextVisibility(false)}
              />
            </Box>
            <Sidebar
              selectedClient={selectedClient}
              data={data}
              innerSideBar={{
                views,
                currentView,
                data: selectedClient,
                pathname,
              }}
            />
          </SideBarContainer>
          {selectedClient && currentView && pathname.split('/').length >= 4 && (
            <Box flex={1}>
              <Box mb={10} textAlign="center">
                <Text fontWeight={700} fontSize={20}>
                  {selectedClient.fullName}
                </Text>
              </Box>
              {currentView.render({ pathname, navigate, canEdit } as any)}
            </Box>
          )}
        </Flex>
      )}
    </Container>
  );
};

export default Vault;
